import React, { useEffect, useMemo, useState } from "react";
import "../styles/App.css";
//import { loadLocal, saveLocal } from "../services/storageBridge";
import {
  loadDashboardSettings,
  saveDashboardSettings,
  ensureAppSettings,
} from "../services/data";
import { db } from "../firebase/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { listenDashboardSettingsHybrid } from "../services/data";

// ---------------------------------
// Utils
// ---------------------------------
const uid = () => Math.random().toString(36).slice(2, 10);
// slugify simple (évite les clés vides si un type n'a pas .key)
const slugify = (s = "") =>
  (s || "")
    .normalize?.("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
// Option spéciale pour regrouper tous les dossiers
const ALL_DOSSIERS_KEY = "__ALL__";
const normalizeTypes = (arr) =>
  Array.isArray(arr)
    ? arr
        .map((t) => {
          const key = t?.key || t?.id || slugify(t?.name || t?.label || "");
          return key ? { ...t, key } : null;
        })
        .filter(Boolean)
    : [];

// ---------------------------------
// Defaults
// ---------------------------------
const DEFAULT_DASHBOARD = {
  layout: { cols: 12, rowHeight: 84, gutter: 12 },
  widgets: [
    {
      id: uid(),
      type: "kpi_count",
      title: "Total documents (tous dossiers)",
      props: { scope: "dossier", dossierKey: "", statut: "" },
      layout: { x: 0, y: 0, w: 3, h: 1, locked: false, hidden: false },
    },
  ],
};

// Description des types et de leurs props configurables
const WIDGET_TYPES = [
  // --- Nouveau type: Agrégat (COUNT / SUM / AVG, GroupBy, Filtre, Affichage) ---
  {
    type: "aggregate",
    label: "Indicateur (KPI / Somme / Moyenne)",
    hint: "Calcule COUNT / SUM / AVG sur un champ, avec GroupBy et Filtre",
    icon: "fa-chart-bar",
    propsSchema: [
      {
        key: "sourceKind",
        label: "Source",
        type: "select",
        options: [
          { value: "dossier", label: "Dossier" },
          { value: "relation", label: "Relation" },
        ],
      },
      // selon la source, on montre l'un ou l'autre
      {
        key: "dossierKey",
        label: "Dossier (ex. factures, contrats)",
        type: "dossierKey",
        visible_if: { sourceKind: "dossier" },
      },
      {
        key: "typeKey",
        label: "Type de relation",
        type: "relationKey",
        visible_if: { sourceKind: "relation" },
      },

      // champ agrégé
      { key: "fieldKey", label: "Champ (key)", type: "fieldKey" },

      // agrégat
      {
        key: "aggregate",
        label: "Agrégat",
        type: "select",
        options: [
          { value: "COUNT", label: "COUNT" },
          { value: "SUM", label: "SUM" },
          { value: "AVG", label: "AVG" },
        ],
      },

      // groupBy
      {
        key: "groupBy",
        label: "GroupBy",
        type: "select",
        options: [
          { value: "day", label: "Jour" },
          { value: "month", label: "Mois" },
          { value: "year", label: "Année" },
          { value: "client", label: "Client" },
        ],
      },

      // filtre (simple texte ex: statut = "Fini")
      {
        key: "filterExpr",
        label: 'Filtre (ex. statut = "Fini")',
        type: "text",
      },

      // affichage
      {
        key: "display",
        label: "Type d'affichage",
        type: "select",
        options: [
          { value: "metric", label: "Chiffre simple" },
          { value: "table", label: "Tableau" },
          { value: "chart", label: "Graphique" },
        ],
      },
    ],
  },
  {
    type: "table_list",
    label: "Tableau - Dossiers",
    hint: "Tableau dédié aux dossiers (recherche, tri, filtres, sommes, échéances)",
    icon: "fa-table",
  },
  {
    type: "relation_table",
    label: "Tableau - Relations",
    hint: "Tableau dédié aux relations (colonnes, sommes, filtres avancés, échéances, seuils, pagination)",
    icon: "fa-list-alt",
    // UI gérée dans WidgetPropsForm (comme table_list)
  },

  // --- Types existants (inchangés) ---
  {
    type: "kpi_count",
    label: "KPI - Compteur",
    hint: "Affiche un nombre (documents ou relations)",
    icon: "fa-chart-pie",
    propsSchema: [
      {
        key: "scope",
        label: "Portée",
        type: "select",
        options: [
          { value: "dossier", label: "Dossiers" },
          { value: "relation", label: "Relations" },
        ],
      },
      { key: "dossierKey", label: "Dossier (optionnel)", type: "dossierKey" },
      {
        key: "typeKey",
        label: "Type de relation (optionnel)",
        type: "relationKey",
      },
      { key: "statut", label: "Statut (optionnel)", type: "text" },
    ],
  },
  {
    type: "recent_records",
    label: "Liste - Derniers documents",
    hint: "Liste les N derniers documents d'un dossier",
    icon: "fa-clock",
    propsSchema: [
      {
        key: "dossierKey",
        label: "Dossier",
        type: "dossierKey",
        required: true,
      },
      {
        key: "limit",
        label: "Nombre d'éléments",
        type: "number",
        min: 1,
        max: 50,
        default: 5,
      },
    ],
  },
  {
    type: "relations_kpi",
    label: "KPI - Relations",
    hint: "Compte le nombre d'entrées pour un type de relation",
    icon: "fa-user-friends",
    propsSchema: [
      {
        key: "typeKey",
        label: "Type de relation",
        type: "relationKey",
        required: true,
      },
    ],
  },
  {
    type: "quick_actions",
    label: "Actions rapides",
    hint: "Boutons pour accéder rapidement aux pages clés",
    icon: "fa-bolt",
    propsSchema: [
      {
        key: "showUpload",
        label: "Afficher 'Nouveau modèle'",
        type: "checkbox",
        default: true,
      },
      {
        key: "showImportRelations",
        label: "Afficher 'Importer CSV (relations)'",
        type: "checkbox",
        default: true,
      },
    ],
  },
  {
    type: "chart_by_month",
    label: "Graph - documents / mois",
    hint: "Agrège les documents par mois (affichage côté Dashboard)",
    icon: "fa-chart-line",
    propsSchema: [
      {
        key: "dossierKey",
        label: "Dossier",
        type: "dossierKey",
        required: true,
      },
      {
        key: "monthsBack",
        label: "Nombre de mois (rétro)",
        type: "number",
        min: 1,
        max: 36,
        default: 12,
      },
    ],
  },
];

function findTypeDef(type) {
  return WIDGET_TYPES.find((t) => t.type === type);
}
// --- Layout par défaut par type (fallback 3x1) ---
function getDefaultLayoutForType(type) {
  switch (type) {
    case "aggregate":
      return { x: 0, y: 0, w: 3, h: 1, locked: false, hidden: false };
    case "table_list":
      return { x: 0, y: 0, w: 12, h: 4, locked: false, hidden: false };
    case "relation_table":
      return { x: 0, y: 0, w: 12, h: 4, locked: false, hidden: false };
    case "kpi_count":
      return { x: 0, y: 0, w: 3, h: 1, locked: false, hidden: false };
    case "relations_kpi":
      return { x: 0, y: 0, w: 3, h: 1, locked: false, hidden: false };
    case "quick_actions":
      return { x: 0, y: 0, w: 4, h: 1, locked: false, hidden: false };
    case "recent_records":
      return { x: 0, y: 0, w: 6, h: 3, locked: false, hidden: false };
    case "chart_by_month":
      return { x: 0, y: 0, w: 6, h: 2, locked: false, hidden: false };
    default:
      return { x: 0, y: 0, w: 3, h: 1, locked: false, hidden: false };
  }
}

// S'assure qu'un widget possède un layout par défaut cohérent avec son type
function ensureDefaultLayout(widget) {
  const base = getDefaultLayoutForType(widget?.type);
  const cur = widget?.layout || {};
  // On ne montre plus ces champs dans l'UI, donc on normalise ici
  return {
    ...widget,
    layout: {
      x: 0,
      y: 0,
      w: typeof cur.w === "number" ? cur.w : base.w,
      h: typeof cur.h === "number" ? cur.h : base.h,
      locked: typeof cur.locked === "boolean" ? cur.locked : base.locked,
      hidden: typeof cur.hidden === "boolean" ? cur.hidden : base.hidden,
    },
  };
}

// opérateurs du builder de filtre
const OPS = ["=", "!=", ">", ">=", "<", "<=", "contains"];
const FILTER_ACTIONS = [
  { value: "filter", label: "Filtrer" },
  { value: "highlight", label: "Colorer" },
];
const DEFAULT_HIGHLIGHT_COLOR = "#ef4444";
const normalizeHighlightColor = (color) => {
  if (typeof color !== "string") return DEFAULT_HIGHLIGHT_COLOR;
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [hash, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return DEFAULT_HIGHLIGHT_COLOR;
};
// --- Statuts standard d'un enregistrement ---
const STATUS_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "Fini", label: "Fini" },
  { value: "Brouillon", label: "Brouillon" },
];
// ---------------------------------
// EditDashboard Page
// ---------------------------------
export default function EditDashboard({ user, onBack }) {
  // Récupérer les types de dossiers / relations pour alimenter les selects
  const localChangeRef = React.useRef(false); // vrai quand on modifie localement
  const suppressRemoteUntil = React.useRef(0); // timestamp jusqu'auquel on ignore les snapshots
  const widgetsHashRef = React.useRef("");
  const [dossierTypes, setDossierTypes] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [dossierFieldsByKey, setDossierFieldsByKey] = useState({}); // { [dossierKey]: [{key,label,type}] }
  const [relationFieldsByTypeKey, setRelationFieldsByTypeKey] = useState({}); // { [typeKey]: [{key,label,type}] }
  const [dossierFieldsRegistryByKey, setDossierFieldsRegistryByKey] = useState(
    {}
  ); // { [dossierKey]: [{key,label,type,usageCount}] }
  const [dossierFieldsUnion, setDossierFieldsUnion] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_DASHBOARD);
  const [widgets, setWidgets] = useState(DEFAULT_DASHBOARD.widgets);
  const [hydrated, setHydrated] = useState(false);
  // Édition inline (sous l'item)
  const [editingId, setEditingId] = useState(null); // id du widget en édition
  const [draft, setDraft] = useState(null); // brouillon du widget en édition
  // Création (formulaire en bas)
  const [createDraft, setCreateDraft] = useState(null); // brouillon pour l'ajout
  const [q, setQ] = useState(""); // recherche texte
  // Normalise & calcule un hash stable (on ignore x/y qui sont toujours 0 chez toi)
  const normalizeWidgets = (arr = []) =>
    (arr || []).map((w) => ensureDefaultLayout(sanitizeWidget(w)));
  function stableStringify(value) {
    const seen = new WeakSet();
    const stable = (v) => {
      if (v && typeof v === "object") {
        if (seen.has(v)) return null; // évite cycles (par sécurité)
        seen.add(v);
        if (Array.isArray(v)) return v.map(stable);
        const out = {};
        Object.keys(v)
          .sort()
          .forEach((k) => {
            out[k] = stable(v[k]);
          });
        return out;
      }
      return v;
    };
    return JSON.stringify(stable(value));
  }
  const hashWidgets = (arr = []) => {
    try {
      const lean = (arr || []).map((w) => ({
        id: w.id,
        type: w.type,
        title: w.title,
        props: w.props || {},
        layout: {
          w: w.layout?.w,
          h: w.layout?.h,
          locked: !!w.layout?.locked,
          hidden: !!w.layout?.hidden,
        },
      }));
      return stableStringify(lean);
    } catch {
      return "";
    }
  };

  // Setter "sûr" pour tout changement local
  const applyLocalWidgets = (updaterOrArray) => {
    setWidgets((prev) => {
      const next =
        typeof updaterOrArray === "function"
          ? updaterOrArray(prev)
          : updaterOrArray;
      const normalized = normalizeWidgets(next);
      const h = hashWidgets(normalized);
      widgetsHashRef.current = h;
      localChangeRef.current = true;
      suppressRemoteUntil.current = Date.now() + 1600;
      return normalized;
    });
  };

  // Persistance
  useEffect(() => {
    if (!user?.uid || !hydrated) return;
    if (!localChangeRef.current) return; // rien à sauvegarder si ça vient du snapshot
    const next = { ...settings, widgets };
    const t = setTimeout(async () => {
      try {
        await saveDashboardSettings(user.uid, next);
      } finally {
        // on laisse encore le snapshot arriver si Firestore re-formate
        setTimeout(() => {
          localChangeRef.current = false;
        }, 200);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [widgets, user?.uid, hydrated, settings]);
  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      // --- Dossiers (types + fields)
      const app = await ensureAppSettings(user.uid);
      const ds = app?.dossiers || null;
      const types = normalizeTypes(ds?.types || []);
      const allFields = ds?.fields || [];
      setDossierTypes(types);

      // map des champs par dossierKey
      const dMap = {};
      for (const t of types) {
        const k = t.key;
        dMap[k] = (allFields || [])
          .filter(
            (f) =>
              !Array.isArray(f.applicableIds) ||
              f.applicableIds.length === 0 ||
              f.applicableIds.includes(k)
          )
          .map((f) => ({
            key: f.key,
            label: f.label || f.name || f.key,
            type: f.type || "text",
          }));
      }
      setDossierFieldsByKey(dMap);

      // --- Relations (si migrées)
      const rs = app?.relations || null; // peut être null/absent
      const rTypes = normalizeTypes(rs?.types || []);
      const rFields = rs?.fields || [];
      setRelationTypes(rTypes);

      const rMap = {};
      for (const t of rTypes) {
        const k = t.key;
        const tid = t.id;
        rMap[k] = (rFields || [])
          .filter((f) => {
            const ids = Array.isArray(f.applicableIds) ? f.applicableIds : [];
            return (
              ids.length === 0 || ids.includes(k) || (tid && ids.includes(tid))
            );
          })
          .map((f) => ({
            key: f.key,
            label: f.label || f.name || f.key,
            type: f.type || "text",
          }));
      }
      setRelationFieldsByTypeKey(rMap);

      // --- Dashboard settings
      const dash = await loadDashboardSettings(user.uid);
      if (dash?.widgets) {
        setSettings(dash);
        setWidgets(
          dash.widgets.map((w) => ensureDefaultLayout(sanitizeWidget(w)))
        );
      } else {
        // seed défaut si absent
        await saveDashboardSettings(user.uid, DEFAULT_DASHBOARD);
        setSettings(DEFAULT_DASHBOARD);
        applyLocalWidgets(DEFAULT_DASHBOARD.widgets);
      }
      setHydrated(true);
    })();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    // [repeat] Écoute en temps réel du registre des champs par dossier
    const colRef = collection(db, "users", user.uid, "dossier_fields");
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const byKey = {};
        snap.forEach((doc) => {
          const data = doc.data() || {};
          const dKey = data.dossierKey || doc.id;
          const fieldsObj = data.fields || {};
          const arr = Object.keys(fieldsObj).map((k) => {
            const f = fieldsObj[k] || {};
            return {
              key: f.key || k,
              label: String(f.label || f.key || k),
              type: String(f.type || "text"),
              usageCount: Number(f.usageCount || 0),
            };
          });
          byKey[dKey] = arr;
        });
        setDossierFieldsRegistryByKey(byKey);

        // [link] Construire l'union multi-dossiers, avec type majoritaire et usageCount cumulé
        const acc = new Map();
        Object.values(byKey).forEach((arr) => {
          (arr || []).forEach((f) => {
            const cur = acc.get(f.key) || {
              key: f.key,
              label: f.label || f.key,
              typeCounts: {},
              usageCount: 0,
            };
            cur.usageCount += Number(f.usageCount || 0);
            cur.typeCounts[f.type] = (cur.typeCounts[f.type] || 0) + 1;
            if (!cur.label && f.label) cur.label = f.label;
            acc.set(f.key, cur);
          });
        });
        const union = Array.from(acc.values())
          .map((e) => {
            const types = Object.entries(e.typeCounts || {});
            const type = types.length
              ? types.sort((a, b) => b[1] - a[1])[0][0]
              : "text";
            return {
              key: e.key,
              label: e.label || e.key,
              type,
              usageCount: e.usageCount || 0,
            };
          })
          .sort(
            (a, b) => b.usageCount - a.usageCount || a.key.localeCompare(b.key)
          );
        setDossierFieldsUnion(union);
      },
      (err) => {
        console.error("listen dossier_fields:", err?.message || err);
        setDossierFieldsRegistryByKey({});
        setDossierFieldsUnion([]);
      }
    );

    return () => unsub();
  }, [user?.uid]);
  useEffect(() => {
    if (!user?.uid) return;
    let first = true;
    const unsub = listenDashboardSettingsHybrid(user.uid, (dash) => {
      const remoteArr = normalizeWidgets(dash?.widgets || []);
      const remoteHash = hashWidgets(remoteArr);
      // [stop] Pendant un court instant après modif locale, on ignore le remote (évite "undo" visuel)
      if (Date.now() < suppressRemoteUntil.current) return;
      // [stop] Si identique à l'état local, ne rien faire (évite les re-renders et les loops)
      if (remoteHash === widgetsHashRef.current) return;
      // [ok] Changement réel distant: on l'applique
      setSettings(dash);
      widgetsHashRef.current = remoteHash;
      setWidgets(remoteArr);
      if (first) {
        setHydrated(true);
        first = false;
      }
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid]);
  // ---------------------------------
  // Actions - CRUD widgets
  // ---------------------------------
  const addWidget = () => {
  const def = WIDGET_TYPES[0];
  const d = ensureDefaultLayout({
    id: uid(),
    type: def.type,
    title: def.label,
    props: buildDefaultProps(def, { dossierTypes, relationTypes }),
  });
  
  // Fermer toute édition existante
  setEditingId(null);
  setDraft(null);
  
  // Ouvrir la modale de création
  setCreateDraft(d);
};

  const editWidget = (w) => {
  const d = JSON.parse(JSON.stringify(w));
  
  // Fermer l'ajout si ouvert
  setCreateDraft(null);
  
  // Ouvrir la modale d'édition
  setEditingId(w.id);
  setDraft(d);
};

  const deleteWidget = (id) => {
    if (!confirm("Supprimer ce widget ?")) return;
    applyLocalWidgets((arr) => arr.filter((w) => w.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setDraft(null);
    }
  };

  const moveWidget = (id, dir) => {
    applyLocalWidgets((arr) => {
      const i = arr.findIndex((w) => w.id === id);
      if (i < 0) return arr;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return arr;
      const copy = arr.slice();
      const tmp = copy[i];
      copy[i] = copy[j];
      copy[j] = tmp;
      return copy;
    });
  };

  const cancelEdit = () => {
    if (editingId) {
      setEditingId(null);
      setDraft(null);
    }
    if (createDraft) {
      setCreateDraft(null);
    }
  };

  const saveEdit = () => {
    // Création (formulaire en bas)
    if (createDraft) {
      const normalized = ensureDefaultLayout(createDraft);
      applyLocalWidgets((arr) => [normalized, ...arr]);
      setCreateDraft(null);
      return;
    }

    // Édition inline (sous l'item)
    if (editingId && draft) {
      const normalized = ensureDefaultLayout(draft);
      applyLocalWidgets((arr) => {
        const i = arr.findIndex((w) => w.id === editingId);
        if (i === -1) return arr;
        const copy = arr.slice();
        copy[i] = normalized;
        return copy;
      });
      setEditingId(null);
      setDraft(null);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return widgets;
    return widgets.filter((w) => {
      const typeDef = findTypeDef(w.type);
      const typeLabel = typeDef?.label || w.type;
      return [w.title, typeLabel, JSON.stringify(w.props)]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [q, widgets]);

  return (
    <div className="container">
      {/* Header */}
      <div
        className="page-title"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <i className="fas fa-sliders-h" />{" "}
          <span>Configurer le Tableau de bord</span>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button
            className="btn btn-outline"
            onClick={() => {
              if (onBack) onBack();
            }}
          >
            <i className="fas fa-arrow-left" /> Retour
          </button>
        </div>
      </div>

      {/* Actions globales */}
      <div
        className="config-actions"
        style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}
      >
        <button className="btn btn-primary" onClick={addWidget}>
          <i className="fas fa-plus" /> Ajouter un widget
        </button>
        <button
          className="btn btn-outline"
          onClick={async () => {
            if (!confirm("Réinitialiser la configuration du tableau de bord ?"))
              return;
            if (user?.uid)
              await saveDashboardSettings(user.uid, DEFAULT_DASHBOARD);
            setSettings(DEFAULT_DASHBOARD);
            applyLocalWidgets(DEFAULT_DASHBOARD.widgets);
          }}
        >
          <i className="fas fa-undo" /> Réinitialiser
        </button>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          <label className="filter-label">Recherche</label>
          <input
            className="filter-input"
            placeholder="Titre, type, props..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {/* Liste des widgets */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Widgets ({widgets.length})</h3>
        </div>
        <div className="config-body">
          <div className="list-items">
            {filtered.length === 0 && (
              <div
                style={{ color: "#6b7280", textAlign: "center", padding: 20 }}
              >
                Aucun widget.
              </div>
            )}

            {filtered.map((w) => {
              const typeDef = findTypeDef(w.type);
              const typeLabel = typeDef?.label || w.type;
              const isEditingThis = editingId === w.id && !!draft;

              return (
                <div
                  key={w.id}
                  id={`w-${w.id}`}
                  style={{ marginBottom: isEditingThis ? 12 : 0 }}
                >
                  {/* Carte / ligne de l'item */}
                  <div className="list-item">
                    <div className="list-item-content">
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div className="list-item-icon">
                          <i className={`fas ${typeDef?.icon || "fa-cube"}`}></i>
                        </div>
                        <div>
                          <div className="list-item-title">
                            {w.title || typeLabel}
                          </div>
                          <div className="list-item-subtitle">
                            Type: {typeLabel}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="list-item-actions">
                      <div
                        className="list-action-btn"
                        title="Monter"
                        onClick={() => moveWidget(w.id, "up")}
                      >
                        <i className="fas fa-arrow-up" />
                      </div>
                      <div
                        className="list-action-btn"
                        title="Descendre"
                        onClick={() => moveWidget(w.id, "down")}
                      >
                        <i className="fas fa-arrow-down" />
                      </div>
                      <div
                        className="list-action-btn"
                        title="Éditer"
                        onClick={() => editWidget(w)}
                      >
                        <i className="fas fa-edit" />
                      </div>
                      <div
                        className="list-action-btn delete"
                        title="Supprimer"
                        onClick={() => deleteWidget(w.id)}
                      >
                        <i className="fas fa-trash" />
                      </div>
                    </div>
                  </div>

                  {/* Formulaire INLINE sous l'item si c'est celui en édition */}
                  {/* MODALE ÉDITION WIDGET */}
{editingId && draft && (
  <div className="formula-helper-overlay show" onClick={(e)=>{ 
    if (e.target.classList.contains('formula-helper-overlay')) {
      setEditingId(null);
      setDraft(null);
    }
  }}>
    <div
      className="formula-helper-modal full-page-form"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="formula-helper-header">
        <h3>
          <i className="fas fa-edit"></i>
          Modifier le widget
        </h3>
        <button className="formula-close-btn" onClick={() => {
          setEditingId(null);
          setDraft(null);
        }}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="formula-helper-content full-page-content">
        <div className="full-page-form-grid">
          {/* Titre */}
          <div className="form-group">
            <label className="form-label full-page-label">Titre</label>
            <input
              className="form-input"
              value={draft.title || ""}
              onChange={(e) =>
                setDraft({ ...draft, title: e.target.value })
              }
              placeholder="Titre du widget"
            />
          </div>

          {/* Type */}
          <div className="form-group">
            <label className="form-label full-page-label">Type de widget</label>
            <select
              className="form-select"
              value={draft.type}
              onChange={(e) => {
                const def = findTypeDef(e.target.value);
                setDraft({
                  ...draft,
                  type: e.target.value,
                  props: buildDefaultProps(
                    def,
                    { dossierTypes, relationTypes },
                    draft.props
                  ),
                });
              }}
            >
              {WIDGET_TYPES.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
            <div style={{ color: "#6b7280", fontSize: "14px", marginTop: "8px" }}>
              {findTypeDef(draft.type)?.hint}
            </div>
          </div>

          {/* Props dynamiques - en pleine largeur */}
          <div className="form-group full-width">
            <WidgetPropsForm
              editing={draft}
              setEditing={setDraft}
              dossierTypes={dossierTypes}
              relationTypes={relationTypes}
              dossierFieldsByKey={dossierFieldsByKey}
              relationFieldsByTypeKey={relationFieldsByTypeKey}
              dossierFieldsRegistryByKey={dossierFieldsRegistryByKey}
              dossierFieldsUnion={dossierFieldsUnion}
            />
          </div>
        </div>

        <div className="modal-footer full-page-footer">
          <button className="btn btn-secondary" onClick={() => {
            setEditingId(null);
            setDraft(null);
          }}>
            <i className="fas fa-times"></i> Annuler
          </button>
          <button className="btn btn-primary" onClick={saveEdit}>
            <i className="fas fa-save"></i> Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>
)}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Formulaire d'AJOUT (en bas seulement) */}
      {/* MODALE AJOUT WIDGET */}
{createDraft && (
  <div className="formula-helper-overlay show" onClick={(e)=>{ 
    if (e.target.classList.contains('formula-helper-overlay')) setCreateDraft(null) 
  }}>
    <div
      className="formula-helper-modal full-page-form"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="formula-helper-header">
        <h3>
          <i className="fas fa-plus"></i>
          Nouveau widget
        </h3>
        <button className="formula-close-btn" onClick={() => setCreateDraft(null)}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="formula-helper-content full-page-content">
        <div className="full-page-form-grid">
          {/* Titre */}
          <div className="form-group">
            <label className="form-label full-page-label">Titre</label>
            <input
              className="form-input"
              value={createDraft.title || ""}
              onChange={(e) =>
                setCreateDraft({ ...createDraft, title: e.target.value })
              }
              placeholder="Donnez un titre à votre widget"
            />
          </div>

          {/* Type */}
          <div className="form-group">
            <label className="form-label full-page-label">Type de widget</label>
            <select
              className="form-select"
              value={createDraft.type}
              onChange={(e) => {
                const def = findTypeDef(e.target.value);
                setCreateDraft({
                  ...createDraft,
                  type: e.target.value,
                  props: buildDefaultProps(
                    def,
                    { dossierTypes, relationTypes },
                    createDraft.props
                  ),
                });
              }}
            >
              {WIDGET_TYPES.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
            <div style={{ color: "#6b7280", fontSize: "14px", marginTop: "8px" }}>
              {findTypeDef(createDraft.type)?.hint}
            </div>
          </div>

          {/* Props dynamiques - en pleine largeur */}
          <div className="form-group full-width">
            <WidgetPropsForm
              editing={createDraft}
              setEditing={setCreateDraft}
              dossierTypes={dossierTypes}
              relationTypes={relationTypes}
              dossierFieldsByKey={dossierFieldsByKey}
              relationFieldsByTypeKey={relationFieldsByTypeKey}
              dossierFieldsRegistryByKey={dossierFieldsRegistryByKey}
              dossierFieldsUnion={dossierFieldsUnion}
            />
          </div>
        </div>

        <div className="modal-footer full-page-footer">
          <button className="btn btn-secondary" onClick={() => setCreateDraft(null)}>
            <i className="fas fa-times"></i> Annuler
          </button>
          <button className="btn btn-primary" onClick={saveEdit}>
            <i className="fas fa-save"></i> Créer le widget
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
}

// ---------------------------------
// Props form renderer (Hooks safe)
// ---------------------------------
function WidgetPropsForm({
  editing,
  setEditing,
  dossierTypes,
  relationTypes,
  dossierFieldsByKey,
  relationFieldsByTypeKey,
  dossierFieldsRegistryByKey,
  dossierFieldsUnion,
}) {
  const typeDef = findTypeDef(editing.type);
  const schema = typeDef?.propsSchema || [];
  const isListTable =
    editing.type === "table_list" || editing.type === "relation_table";
  const forcedRelation = editing.type === "relation_table";
  // src = 'relation' si relation_table, sinon lit depuis props (défaut dossier)
  // nouveau - laisse la source vide si non choisie (force l'utilisateur à choisir)
  const src = forcedRelation ? "relation" : (editing?.props?.sourceKind ?? "");
  const setProp = (k, v) =>
    setEditing({ ...editing, props: { ...editing.props, [k]: v } });

  // ====== AGGREGATE: hooks déclarés hors condition ======
  const knownFields = React.useMemo(() => {
    if (editing.type !== "aggregate") return [];
    if (src === "relation") {
      return relationFieldsByTypeKey?.[editing?.props?.typeKey] || [];
    }
    const dk = editing?.props?.dossierKey || "";
    if (dk === ALL_DOSSIERS_KEY) return dossierFieldsUnion || [];
    // priorité au registre Firestore, sinon fallback "settings"
    return dossierFieldsRegistryByKey?.[dk] || dossierFieldsByKey?.[dk] || [];
  }, [
    editing.type,
    src,
    editing?.props?.typeKey,
    editing?.props?.dossierKey,
    dossierFieldsByKey,
    relationFieldsByTypeKey,
    dossierFieldsRegistryByKey,
    dossierFieldsUnion,
  ]);

  React.useEffect(() => {
    if (editing.type !== "aggregate") return;
    const current = editing?.props?.filter?.field || "";
    if (!knownFields.length) return;
    const exists = knownFields.some((f) => f.key === current);
    if (!current || !exists) {
      setProp("filter", {
        ...(editing.props?.filter || {}),
        field: knownFields[0].key,
      });
    }
  }, [editing.type, knownFields]); // eslint-disable-line react-hooks/exhaustive-deps
  // Si la clé agrégée n'existe plus dans le nouveau contexte (changement de dossier),
  // on la vide pour éviter un état incohérent.
  React.useEffect(() => {
    if (editing.type !== "aggregate") return;
    const fk = editing?.props?.fieldKey || "";
    if (!fk) return;
    const exists = (knownFields || []).some((f) => f.key === fk);
    if (!exists) setProp("fieldKey", "");
  }, [editing.type, knownFields]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (
      editing.type === "table_list" &&
      editing?.props?.sourceKind !== "dossier"
    ) {
      setProp("sourceKind", "dossier");
    }
  }, [editing.type]);

  // ====== TABLE_LIST: hooks déclarés hors condition ======
  const availableFields = React.useMemo(() => {
    if (!isListTable) return [];
    if (src === "relation") {
      return relationFieldsByTypeKey?.[editing?.props?.typeKey] || [];
    }
    const dk = editing?.props?.dossierKey || "";
    if (dk === ALL_DOSSIERS_KEY) return dossierFieldsUnion || [];
    return dossierFieldsRegistryByKey?.[dk] || dossierFieldsByKey?.[dk] || [];
  }, [
    isListTable,
    src,
    editing?.props?.typeKey,
    editing?.props?.dossierKey,
    dossierFieldsByKey,
    relationFieldsByTypeKey,
    dossierFieldsRegistryByKey,
    dossierFieldsUnion,
  ]);

  const availableDateFields = React.useMemo(() => {
    if (!isListTable) return [];
    const arr = availableFields || [];
    const datey = arr.filter(
      (f) =>
        String(f.type).toLowerCase() === "date" ||
        /date|echeance|échéance|deadline|due|expiration|expiry/i.test(
          f.key || f.label || ""
        )
    );
    return datey.length ? datey : arr;
  }, [isListTable, availableFields]);

  // Helpers colonnes CSV <-> array (utilisés par table_list)
  const parseCols = (s) =>
    (s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  const toCsv = (arr) => (arr || []).join(",");

  const colKeys = parseCols(editing.props?.columnsKeys);
  const sumKeys = Array.isArray(editing.props?.sumKeys)
    ? editing.props.sumKeys
    : [];

  // État local pour l'ajout de colonne (toujours déclaré)
  const [colToAdd, setColToAdd] = React.useState("");

  // Actions colonnes (table_list)
  const addCol = (k) => {
    if (!k) return;
    if (colKeys.includes(k)) return;
    const next = [...colKeys, k];
    setProp("columnsKeys", toCsv(next));
  };
  const removeCol = (k) => {
    const next = colKeys.filter((x) => x !== k);
    setProp("columnsKeys", toCsv(next));
    if (sumKeys.includes(k))
      setProp(
        "sumKeys",
        sumKeys.filter((x) => x !== k)
      ); // nettoie orphelin
  };
  const moveCol = (k, dir) => {
    const i = colKeys.indexOf(k);
    if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= colKeys.length) return;
    const copy = colKeys.slice();
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
    setProp("columnsKeys", toCsv(copy));
  };
  const toggleSumKey = (k) => {
    const has = sumKeys.includes(k);
    setProp("sumKeys", has ? sumKeys.filter((x) => x !== k) : [...sumKeys, k]);
  };

  // Filtres avancés (table_list)
  const addFilter = () => {
    const arr = Array.isArray(editing.props?.filters)
      ? editing.props.filters.slice()
      : [];
    arr.push({
      field: "",
      op: "=",
      value: "",
      action: "filter",
      color: DEFAULT_HIGHLIGHT_COLOR,
    });
    setProp("filters", arr);
  };
  const updateFilter = (i, patch) => {
    const arr = Array.isArray(editing.props?.filters)
      ? editing.props.filters.slice()
      : [];
    const next = { ...(arr[i] || {}), ...patch };
    const action = (next.action || "filter").toLowerCase();
    if (action === "highlight" && !next.color) {
      next.color = DEFAULT_HIGHLIGHT_COLOR;
    }
    arr[i] = next;
    setProp("filters", arr);
  };
  const removeFilter = (i) => {
    const arr = Array.isArray(editing.props?.filters)
      ? editing.props.filters.slice()
      : [];
    arr.splice(i, 1);
    setProp("filters", arr);
  };

  // Nettoyage quand source/dossier/type change (table_list)
  React.useEffect(() => {
    if (editing.type !== "table_list") return;
    const availKeys = new Set((availableFields || []).map((f) => f.key));
    if (!colKeys.length && !sumKeys.length) return;

    const fixedCols = colKeys.filter((k) => availKeys.has(k));
    const fixedSums = sumKeys.filter((k) => availKeys.has(k));

    if (toCsv(fixedCols) !== (editing.props?.columnsKeys || "")) {
      setProp("columnsKeys", toCsv(fixedCols));
    }
    if (JSON.stringify(fixedSums) !== JSON.stringify(sumKeys)) {
      setProp("sumKeys", fixedSums);
    }

    if (colToAdd && !availKeys.has(colToAdd)) setColToAdd("");
  }, [editing.type, availableFields]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!isListTable) return;
    const availKeys = new Set((availableFields || []).map((f) => f.key));
    if (!colKeys.length && !sumKeys.length) return;

    const fixedCols = colKeys.filter((k) => availKeys.has(k));
    const fixedSums = sumKeys.filter((k) => availKeys.has(k));

    if (toCsv(fixedCols) !== (editing.props?.columnsKeys || "")) {
      setProp("columnsKeys", toCsv(fixedCols));
    }
    if (JSON.stringify(fixedSums) !== JSON.stringify(sumKeys)) {
      setProp("sumKeys", fixedSums);
    }
    if (colToAdd && !availKeys.has(colToAdd)) setColToAdd("");
  }, [isListTable, availableFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // ================= RENDUS =================

  // ---- Carte KPI (aggregate) ----
  if (editing.type === "aggregate") {
    return (
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Propriétés - Carte KPI</h3>
        </div>
        <div className="config-body" >
          <h3 className="section-header">Configuration de base</h3>
          <div className="form-grid form-grid-2">
            {/* Source */}
            {!forcedRelation && editing.type === "table_list"
              ? null
              : !forcedRelation && (
                  <div className="form-group">
                    <label className="form-label">
                      Source <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <select
                      className="form-input"
                      value={src}
                      onChange={(e) => setProp("sourceKind", e.target.value)}
                      title="Sélectionnez la source des données (Dossier ou Relation)"
                    >
                      <option value="">- Choisir -</option>
                      <option value="dossier">Dossier</option>
                      <option value="relation">Relation</option>
                    </select>
                  </div>
                )}
            {/* Dossier / Type */}
            {src === "dossier" && (
              <div className="form-group">
                <label className="form-label">
                  Dossier/Type <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className="form-input"
                  value={editing.props?.dossierKey || ""}
                  onChange={(e) => setProp("dossierKey", e.target.value)}
                  title="Choisissez le dossier source des données"
                >
                  <option value="">- Choisir -</option>
                  <option value={ALL_DOSSIERS_KEY}>Tous les dossiers</option>
                  {dossierTypes.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.name || d.label || d.key} ({d.key})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {src === "relation" && (
              <div className="form-group">
                <label className="form-label">
                  Type de relation <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className="form-input"
                  value={editing.props?.typeKey || ""}
                  onChange={(e) => setProp("typeKey", e.target.value)}
                  title="Choisissez le type de relation source"
                >
                  <option value="">- Choisir -</option>
                  {relationTypes.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name || t.label || t.key} ({t.key})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Période */}
            <div className="form-group">
              <label className="form-label">Période</label>
              <select
                className="form-input"
                value={editing.props?.period || "all"}
                onChange={(e) => setProp("period", e.target.value)}
                title="Filtrez les données par période temporelle"
              >
                <option value="all">Toutes les données</option>
                <option value="day">Jour (aujourd'hui)</option>
                <option value="month">Mois (en cours)</option>
                <option value="year">Année (en cours)</option>
              </select>
            </div>
            {/* Statut (optionnel) */}
            <div className="form-group">
              <label className="form-label">Statut</label>
              <select
                className="form-input"
                value={editing.props?.statut ?? ""}
                onChange={(e) => setProp("statut", e.target.value)}
                title="Filtre rapide sur le statut des enregistrements"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                Filtre rapide sur le champ <code>statut</code> des
                enregistrements (ex: Fini / Brouillon).
              </div>
            </div>
          </div>

          <h3 className="section-header">Agrégation</h3>
          <div className="form-grid form-grid-2">
            {/* Agrégat */}
            <div className="form-group full-width">
              <label className="form-label">
                Type d'agrégat <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                className="form-input"
                value={editing.props?.aggregate || "COUNT"}
                onChange={(e) => setProp("aggregate", e.target.value)}
                title="Choisissez le type d'opération d'agrégation"
              >
                <option value="COUNT">COUNT</option>
                <option value="SUM">SUM</option>
                <option value="AVG">AVG</option>
              </select>
            </div>

            {/* Champ (key) */}
            <div className="form-group full-width">
              <label className="form-label">Champ à agréger</label>
              <select
                className="form-input"
                disabled={knownFields.length === 0}
                value={editing.props?.fieldKey || ""}
                onChange={(e) => setProp("fieldKey", e.target.value)}
                title="Sélectionnez le champ numérique à agréger (requis pour SUM/AVG)"
              >
                {knownFields.length === 0 ? (
                  <option value="">(Non requis)</option>
                ) : (
                  <>
                    <option value="">- Sélectionner un champ -</option>
                    {knownFields
                      .sort(
                        (a, b) =>
                          (b.usageCount || 0) - (a.usageCount || 0) ||
                          String(a.label || a.key).localeCompare(
                            String(b.label || b.key)
                          )
                      )
                      .map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label || f.key} ({f.key})
                        </option>
                      ))}
                  </>
                )}
              </select>
              {knownFields.length === 0 &&
                editing.props?.aggregate !== "COUNT" && (
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                    Aucun schéma de champs détecté pour cette source.
                  </div>
                )}
            </div>
          </div>

          <h3 className="section-header">Affichage</h3>
          <div className="form-grid form-grid-2">
            {/* Libellé secondaire */}
            <div className="form-group full-width">
              <label className="form-label">Libellé secondaire</label>
              <input
                className="form-input"
                value={editing.props?.labelText || ""}
                onChange={(e) => setProp("labelText", e.target.value)}
                placeholder="Ex. Factures - Fini"
                title="Texte complémentaire affiché sous la valeur"
              />
            </div>
          </div>

          <h3 className="section-header">Filtres avancés</h3>
          <div className="form-grid form-grid-2">
            {/* Filtre (optionnel) */}
            <div className="form-group full-width">
              <label className="form-label">Filtre avancé (optionnel)</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr 0.8fr 1.2fr",
                  gap: 8,
                }}
              >
                <select
                  className="form-input"
                  disabled={
                    (src === "dossier" && !editing.props?.dossierKey) ||
                    (src === "relation" && !editing.props?.typeKey)
                  }
                  value={editing.props?.filter?.field || ""}
                  onChange={(e) =>
                    setProp("filter", {
                      ...(editing.props?.filter || {}),
                      field: e.target.value,
                    })
                  }
                  title="Champ sur lequel appliquer le filtre"
                >
                  <option value="">
                    {src === "dossier" && !editing.props?.dossierKey
                      ? "- Choisir un dossier -"
                      : src === "relation" && !editing.props?.typeKey
                      ? "- Choisir un type -"
                      : "- Champ -"}
                  </option>
                  {knownFields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label} ({f.key})
                    </option>
                  ))}
                </select>
                <select
                  className="form-input"
                  value={editing.props?.filter?.op || "="}
                  onChange={(e) =>
                    setProp("filter", {
                      ...(editing.props?.filter || {}),
                      op: e.target.value,
                    })
                  }
                  title="Opérateur de comparaison"
                >
                  {OPS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
                <input
                  className="form-input"
                  value={editing.props?.filter?.value ?? ""}
                  onChange={(e) =>
                    setProp("filter", {
                      ...(editing.props?.filter || {}),
                      value: e.target.value,
                    })
                  }
                  placeholder="Valeur..."
                  title="Valeur de comparaison"
                />
              </div>
              {knownFields.length === 0 && (
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                  Aucun schéma de champs détecté pour cette source - la
                  configuration du filtre sera enregistrée.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Tableau (table_list) ----
  if (isListTable) {
    return (
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Propriétés - Tableau</h3>
        </div>
        <div className="config-body" >
          <h3 className="section-header">Configuration générale</h3>
          <div className="form-grid form-grid-2">
            {/* Source cachée pour table_list ; visible uniquement si relation_table */}
            {!forcedRelation && editing.type !== "table_list" && (
              <div className="form-group">
                <label className="form-label">Source</label>
                <select
                  className="form-input"
                  value={src}
                  onChange={(e) => setProp("sourceKind", e.target.value)}
                >
                  <option value="">- Choisir -</option>
                  <option value="dossier">Dossier</option>
                  <option value="relation">Relation</option>
                </select>
              </div>
            )}

            {/* Dossier / Type */}
            {src === "dossier" && !forcedRelation && (
              <div className="form-group">
                <label className="form-label">Dossier</label>
                <select
                  className="form-input"
                  value={editing.props?.dossierKey || ""}
                  onChange={(e) => setProp("dossierKey", e.target.value)}
                >
                  <option value="">- Choisir -</option>
                  <option value={ALL_DOSSIERS_KEY}>Tous les dossiers</option>
                  {dossierTypes.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.name || d.label || d.key} ({d.key})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(src === "relation" || forcedRelation) && (
              <div className="form-group">
                <label className="form-label">Type de relation</label>
                <select
                  className="form-input"
                  value={editing.props?.typeKey || ""}
                  onChange={(e) => setProp("typeKey", e.target.value)}
                >
                  <option value="">- Choisir -</option>
                  {relationTypes.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name || t.label || t.key} ({t.key})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Période */}
            <div className="form-group">
              <label className="form-label">Période</label>
              <select
                className="form-input"
                value={editing.props?.period || "all"}
                onChange={(e) => setProp("period", e.target.value)}
              >
                <option value="all">Toutes les données</option>
                <option value="day">Jour (aujourd'hui)</option>
                <option value="month">Mois (en cours)</option>
                <option value="year">Année (en cours)</option>
              </select>
            </div>
            {/* Statut (optionnel) */}
            {src === "dossier" && !forcedRelation && (
              <div className="form-group">
                <label className="form-label">Statut</label>
                <select
                  className="form-input"
                  value={editing.props?.statut ?? ""}
                  onChange={(e) => setProp("statut", e.target.value)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                  Appliqué en plus des Filtres avancés (si définis).
                </div>
              </div>
            )}
          </div>

          <h3 className="section-header">Colonnes et totaux</h3>
          <div className="form-grid form-grid-2">
            {/* Colonnes */}
            <div className="form-group full-width">
              <label className="form-label">Colonnes</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 8,
                }}
              >
                <select
                  className="form-input"
                  value={colToAdd}
                  onChange={(e) => setColToAdd(e.target.value)}
                  disabled={!availableFields.length}
                >
                  <option value="">
                    {availableFields.length
                      ? "- Choisir un champ -"
                      : "(Aucun champ disponible)"}
                  </option>
                  {availableFields
                    .sort(
                      (a, b) =>
                        (b.usageCount || 0) - (a.usageCount || 0) ||
                        String(a.label || a.key).localeCompare(
                          String(b.label || b.key)
                        )
                    )
                    .map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label || f.key} ({f.key})
                      </option>
                    ))}
                </select>
                <button
                  className="btn btn-outline"
                  onClick={() => addCol(colToAdd)}
                  disabled={!colToAdd}
                >
                  <i className="fas fa-plus" /> Ajouter
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                {colKeys.map((k) => {
                  const f = (availableFields || []).find((x) => x.key === k);
                  const label = f?.label || k;
                  return (
                    <div key={k} className="chip">
                      <span style={{ marginRight: 8 }}>
                        {label} <span style={{ opacity: 0.6 }}>({k})</span>
                      </span>
                      <button
                        className="chip-btn"
                        onClick={() => moveCol(k, "up")}
                        title="Monter"
                      >
                        <i className="fas fa-arrow-up" />
                      </button>
                      <button
                        className="chip-btn"
                        onClick={() => moveCol(k, "down")}
                        title="Descendre"
                      >
                        <i className="fas fa-arrow-down" />
                      </button>
                      <button
                        className="chip-btn danger"
                        onClick={() => removeCol(k)}
                        title="Retirer"
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  );
                })}
                {!colKeys.length && (
                  <div style={{ color: "#6b7280" }}>
                    Aucune colonne sélectionnée.
                  </div>
                )}
              </div>

              {colKeys.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}
                  >
                    Sommes par colonne
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {colKeys.map((k) => (
                      <label
                        key={k}
                        className="form-checkbox"
                        style={{
                          display: "inline-flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={sumKeys.includes(k)}
                          onChange={() => toggleSumKey(k)}
                        />
                        <span>{k}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <h3 className="section-header">Filtres avancés</h3>
          <div className="form-grid form-grid-2">
            {/* Filtres avancés */}
            <div className="form-group">
              <label className="form-label">Filtres avancés</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(editing.props?.filters || []).map((f, i) => {
                  const action = (f?.action || "filter").toLowerCase();
                  const colorValue = normalizeHighlightColor(f?.color);
                  return (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "1.2fr 0.8fr 1.2fr 1fr 0.7fr auto",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <input
                        className="form-input"
                        placeholder="Champ (clé)"
                        value={f.field || ""}
                        onChange={(e) =>
                          updateFilter(i, { field: e.target.value })
                        }
                      />
                      <select
                        className="form-input"
                        value={f.op || "="}
                        onChange={(e) => updateFilter(i, { op: e.target.value })}
                      >
                        {OPS.map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </select>
                      <input
                        className="form-input"
                        placeholder="Valeur"
                        value={f.value ?? ""}
                        onChange={(e) =>
                          updateFilter(i, { value: e.target.value })
                        }
                      />
                      <select
                        className="form-input"
                        value={action}
                        onChange={(e) =>
                          updateFilter(i, { action: e.target.value })
                        }
                      >
                        {FILTER_ACTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="color"
                        value={colorValue}
                        onChange={(e) =>
                          updateFilter(i, { color: e.target.value })
                        }
                        disabled={action !== "highlight"}
                        title="Couleur à appliquer"
                        style={{
                          width: "100%",
                          height: 38,
                          padding: 4,
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          backgroundColor:
                            action === "highlight" ? "#fff" : "#f3f4f6",
                          cursor:
                            action === "highlight" ? "pointer" : "not-allowed",
                        }}
                      />
                      <button
                        className="btn btn-outline"
                        onClick={() => removeFilter(i)}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  );
                })}
                <button className="btn btn-outline" onClick={addFilter}>
                  <i className="fas fa-plus" /> Ajouter un filtre
                </button>
              </div>
            </div>
          </div>

          <h3 className="section-header">Échéances et pagination</h3>
          <div className="form-grid form-grid-2">
            {/* Deadline / Échéance */}
            <div className="form-group">
              <label className="form-label">Deadline / Échéance</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr",
                  gap: 8,
                }}
              >
                <select
                  className="form-input"
                  value={editing.props?.deadlineKey || ""}
                  onChange={(e) => setProp("deadlineKey", e.target.value)}
                  disabled={!availableDateFields.length}
                >
                  <option value="">
                    {availableDateFields.length
                      ? "- Aucun -"
                      : "(Aucun champ disponible)"}
                  </option>
                  {availableDateFields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label || f.key} ({f.key})
                    </option>
                  ))}
                </select>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  placeholder="warnDays"
                  value={editing.props?.warnDays ?? 7}
                  onChange={(e) =>
                    setProp(
                      "warnDays",
                      Math.max(0, Number(e.target.value) || 0)
                    )
                  }
                />
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  placeholder="dangerDays"
                  value={editing.props?.dangerDays ?? 2}
                  onChange={(e) =>
                    setProp(
                      "dangerDays",
                      Math.max(0, Number(e.target.value) || 0)
                    )
                  }
                />
                <label
                  className="form-checkbox"
                  style={{
                    display: "inline-flex",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!editing.props?.blink}
                    onChange={(e) => setProp("blink", !!e.target.checked)}
                  />
                  <span>Clignoter</span>
                </label>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                À J-≤ dangerDays → rouge (peut clignoter). À J-≤ warnDays →
                orange.
              </div>
            </div>
            {/* Nombre de lignes par page */}
            <div className="form-group">
              <label className="form-label">Nombre de lignes par page</label>
              <input
                className="form-input"
                type="number"
                min={1}
                max={1000}
                value={Number(editing.props?.pageSize ?? 7)}
                onChange={(e) =>
                  setProp("pageSize", Math.max(1, Number(e.target.value) || 7))
                }
              />
            </div>
          </div>

          <h3 className="section-header">Alertes de seuil</h3>
          <div className="form-grid form-grid-2">
            {/* Seuils (alertes sur colonnes numériques) */}
            <div className="form-group">
              <label className="form-label">
                Seuils (alerte si valeur &lt; min)
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(editing.props?.thresholds || []).map((t, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.5fr 1fr auto",
                      gap: 8,
                    }}
                  >
                    <select
                      className="form-input"
                      value={t.field || ""}
                      onChange={(e) => {
                        const arr = [...(editing.props?.thresholds || [])];
                        arr[i] = { ...(arr[i] || {}), field: e.target.value };
                        setProp("thresholds", arr);
                      }}
                    >
                      <option value="">- Champ -</option>
                      {(availableFields || []).map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label || f.key} ({f.key})
                        </option>
                      ))}
                    </select>
                    <input
                      className="form-input"
                      type="number"
                      placeholder="min"
                      value={t.min ?? ""}
                      onChange={(e) => {
                        const arr = [...(editing.props?.thresholds || [])];
                        arr[i] = {
                          ...(arr[i] || {}),
                          min: Number(e.target.value),
                        };
                        setProp("thresholds", arr);
                      }}
                    />
                    <button
                      className="btn btn-outline"
                      onClick={() => {
                        const arr = [...(editing.props?.thresholds || [])];
                        arr.splice(i, 1);
                        setProp("thresholds", arr);
                      }}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    const arr = [...(editing.props?.thresholds || [])];
                    arr.push({ field: "", min: 0 });
                    setProp("thresholds", arr);
                  }}
                >
                  <i className="fas fa-plus" /> Ajouter un seuil
                </button>
              </div>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                Exemple: <code>stock</code> &lt; <code>10</code> déclenche une
                alerte visuelle (prévention de rupture).
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Rendu générique pour les autres types ----
  const shouldShow = (f) => {
    if (!f?.visible_if) return true;
    const cond = f.visible_if;
    return Object.entries(cond).every(
      ([k, v]) => String(editing?.props?.[k] ?? "") === String(v)
    );
  };

  return (
    <div className="config-section">
      <div className="config-header">
        <h3 className="config-title">Propriétés</h3>
      </div>
      <div className="config-body" >
        <div className="config-form props-grid">
          {schema.length === 0 && (
            <div style={{ color: "#6b7280" }}>
              Aucune propriété spécifique pour ce type.
            </div>
          )}
          {schema.filter(shouldShow).map((f) => {
            const key = f.key;
            const val = editing.props?.[key];

            if (f.type === "text")
              return (
                <div key={key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <input
                    className="form-input"
                    value={val || ""}
                    onChange={(e) => setProp(key, e.target.value)}
                  />
                </div>
              );
            if (f.type === "number")
              return (
                <div key={key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <input
                    className="form-input"
                    type="number"
                    value={val ?? 0}
                    onChange={(e) => setProp(key, Number(e.target.value))}
                  />
                </div>
              );
            if (f.type === "checkbox")
              return (
                <div key={key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <label
                    className="form-checkbox"
                    style={{
                      display: "inline-flex",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!val}
                      onChange={(e) => setProp(key, !!e.target.checked)}
                    />
                    <span>Activer</span>
                  </label>
                </div>
              );
            if (f.type === "select")
              return (
                <div key={key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <select
                    className="form-input"
                    value={val ?? f.options?.[0]?.value ?? ""}
                    onChange={(e) => setProp(key, e.target.value)}
                  >
                    {(f.options || []).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            // --- Sélecteur Dossier (pour kpi_count & co) ---
            if (f.type === "dossierKey")
              return (
                <div key={key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <select
                    className="form-input"
                    value={val || ""}
                    onChange={(e) => setProp(key, e.target.value)}
                  >
                    <option value="">- Choisir -</option>
                    {dossierTypes.map((d) => (
                      <option key={d.key} value={d.key}>
                        {d.name || d.label || d.key} ({d.key})
                      </option>
                    ))}
                  </select>
                </div>
              );

            // --- Sélecteur Relation (pour kpi_count & co) ---
            if (f.type === "relationKey")
              return (
                <div key={key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <select
                    className="form-input"
                    value={val || ""}
                    onChange={(e) => setProp(key, e.target.value)}
                  >
                    <option value="">- Choisir -</option>
                    {relationTypes.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.name || t.label || t.key} ({t.key})
                      </option>
                    ))}
                  </select>
                </div>
              );

            return null;
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------
// Helpers
// ---------------------------------

function buildDefaultProps(
  def,
  { dossierTypes, relationTypes },
  currentProps = {}
) {
  const out = { ...(currentProps || {}) };

  // Defaults dédiés à la "Carte KPI (agrégat)"
  if (def?.type === "aggregate") {
    if (out.display === undefined) out.display = "metric";    
    if (out.dossierKey === undefined) out.dossierKey = "";
    if (out.typeKey === undefined) out.typeKey = "";
    if (out.period === undefined) out.period = "all"; // 'all' | 'day' | 'month' | 'year'
    if (out.aggregate === undefined) out.aggregate = "COUNT"; // 'COUNT' | 'SUM' | 'AVG'
    if (out.fieldKey === undefined) out.fieldKey = ""; // libre
    if (out.filter === undefined) out.filter = null; // { field, op, value } ou null
    if (out.statut === undefined) out.statut = "";
    const current = out.labelText;
    out.labelText = current && current !== "test_config" ? current : "";
    return out;
  }
  // Defaults dédiés au "Tableau (table_list)"
  if (def?.type === "table_list") {
    if (out.sourceKind === undefined) out.sourceKind = "dossier";
    if (out.dossierKey === undefined) out.dossierKey = dossierTypes?.[0]?.key || "";
    if (out.typeKey === undefined) out.typeKey = relationTypes?.[0]?.key || "";
    if (out.period === undefined) out.period = "all";
    if (out.columnsKeys === undefined) out.columnsKeys = "";
    if (out.sumKeys === undefined) out.sumKeys = [];
    if (out.filters === undefined) out.filters = [];
    if (out.deadlineKey === undefined) out.deadlineKey = "";
    if (out.warnDays === undefined) out.warnDays = 7;
    if (out.dangerDays === undefined) out.dangerDays = 2;
    if (out.blink === undefined) out.blink = true;
    if (out.statut === undefined) out.statut = "";
    // [new]
    if (out.pageSize === undefined) out.pageSize = 7;
    if (out.thresholds === undefined) out.thresholds = []; // [{ field:'stock', min:10 }]
    return out;
  }

  // Defaults dédiés au "Tableau - Relations" (relation_table)
  if (def?.type === "relation_table") {
    if (out.sourceKind === undefined) out.sourceKind = "relation"; // [lock] forcé relations
    if (out.typeKey === undefined) out.typeKey = relationTypes?.[0]?.key || "";
    if (out.period === undefined) out.period = "all";
    if (out.columnsKeys === undefined) out.columnsKeys = "";
    if (out.sumKeys === undefined) out.sumKeys = [];
    if (out.filters === undefined) out.filters = [];
    if (out.deadlineKey === undefined) out.deadlineKey = "";
    if (out.warnDays === undefined) out.warnDays = 7;
    if (out.dangerDays === undefined) out.dangerDays = 2;
    if (out.blink === undefined) out.blink = true;
    if (out.pageSize === undefined) out.pageSize = 7;
    if (out.thresholds === undefined) out.thresholds = []; // [{ field:'stock', min:10 }]
    return out;
  }

  // Fallback générique (inchangé) pour les autres types
  (def?.propsSchema || []).forEach((f) => {
    if (out[f.key] !== undefined) return;
    if (f.type === "checkbox") out[f.key] = f.default ?? false;
    else if (f.type === "number") out[f.key] = f.default ?? 0;
    else if (f.type === "select") out[f.key] = f.options?.[0]?.value ?? "";
    else if (f.type === "dossierKey") out[f.key] = dossierTypes?.[0]?.key ?? "";
    else if (f.type === "relationKey")
      out[f.key] = relationTypes?.[0]?.key ?? "";
    else if (f.type === "fieldKey") out[f.key] = "";
    else out[f.key] = "";
  });
  return out;
}
function sanitizeWidget(w) {
  if (w?.type === "aggregate" && w?.props?.labelText === "test_config") {
    return { ...w, props: { ...w.props, labelText: "" } };
  }
  return w;
}
